import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import settingsConfig from '@/modules/settings/config'
import request from '@/modules/base/util/request'

type AccountSettingsData = Record<string, unknown>

type AccountSettingsContextValue = {
  update: (data: AccountSettingsData) => Promise<void>
  refresh: () => Promise<unknown>
  isLoading: boolean
  [key: string]: unknown
}

type AccountSettingProviderProps = {
  http: { post: (url: string, config: Record<string, unknown>) => Promise<any> }
  children: ReactNode
  initData?: AccountSettingsData
  collaborationId?: string
}

export const AccountSettingsContext = createContext<AccountSettingsContextValue>({} as AccountSettingsContextValue)

const AccountSettingProvider = (props: AccountSettingProviderProps) => {
  const { http, children, initData, collaborationId } = props
  const headers: Record<string, string> = {}
  if (collaborationId) headers['collaboration-id'] = collaborationId

  const [data, setData] = useState(initData)
  const [isLoading, setIsLoading] = useState(!initData)

  useEffect(() => { if (!initData) refresh() }, [])

  const refresh = () => {
    return request(`${settingsConfig.account}`, { auth: true, isConcurrent: true, headers })
      .then((response: any) => {
        const { body: { data: accountModelData } } = response
        setData(accountModelData)
      })
      .done(() => setIsLoading(false))
  }

  const update = (data: AccountSettingsData): Promise<void> => {
    return new Promise((resolve, reject) => {
      http.post(
        settingsConfig.account, {
          method: 'POST',
          auth: true,
          isConcurrent: true,
          data,
          headers,
        },
      ).then((response: any) => {
        const { body: { data: accountModelData } } = response
        setData(accountModelData)
        resolve()
      })
        .catch((error: unknown) => reject(error))
    })
  }

  return (
    <AccountSettingsContext.Provider value={{ update, refresh, isLoading, ...data }}>{children}</AccountSettingsContext.Provider>
  )
}

export const useAccountSettings = () => useContext(AccountSettingsContext)
export default AccountSettingProvider
